import React, { useState } from 'react';
import { Globe, ChevronDown, X, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', flag: '🇺🇸' },
  { code: 'es', flag: '🇪🇸' },
  { code: 'fr', flag: '🇫🇷' },
  { code: 'de', flag: '🇩🇪' },
  { code: 'pt', flag: '🇧🇷' },
  { code: 'ja', flag: '🇯🇵' },
  { code: 'zh', flag: '🇨🇳' },
  { code: 'ko', flag: '🇰🇷' },
  { code: 'ar', flag: '🇸🇦' },
  { code: 'hi', flag: '🇮🇳' }
];

// Check if running in iOS native app
const isNativeApp = () => {
  return !!(window.webkit?.messageHandlers?.saveImage);
};

const LanguageSelector = ({ className = '' }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = isNativeApp() || window.innerWidth < 640;

  const languages = SUPPORTED_LANGUAGES.map((lang) => ({
    ...lang,
    name: t(`languages.${lang.code}`)
  }));
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  // Document direction/lang is kept in sync globally in i18n.js
  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white/80 hover:bg-white rounded-lg transition text-sm font-medium text-gray-700 border border-gray-200"
        aria-label={t('languageSelector.selectLanguage')}
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{currentLang.flag} {currentLang.name}</span>
        <span className="sm:hidden">{currentLang.flag}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Mobile: Bottom sheet modal */}
          {isMobile ? (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50">
              <div className="bg-white rounded-t-2xl w-full max-w-md safe-area-bottom">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">{t('languageSelector.selectLanguage')}</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-4 text-left hover:bg-purple-50 transition flex items-center gap-4 border-b border-gray-100 ${
                        i18n.language === lang.code ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-2xl">{lang.flag}</span>
                      <span className="text-base font-medium">{lang.name}</span>
                      {i18n.language === lang.code && (
                        <CheckCircle className="w-5 h-5 ml-auto text-purple-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Desktop: Dropdown */
            <>
              <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 max-h-80 overflow-y-auto">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full px-4 py-2 text-left hover:bg-purple-50 transition flex items-center gap-3 ${
                      i18n.language === lang.code ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="text-sm font-medium">{lang.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
